// node_modules
import { ChangeEvent, FC, useCallback, useState } from "react";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Types
import { TActionObjectDTO } from "Types";
// Components
import { FindestSelectSearchPriority } from "Components";
import { Synonyms } from "../Synonyms/Synonyms";
import { SearchSubTerm } from "../SearchSubTerm/SearchSubTerm";
// Enums
import { SearchPriorityEnum, SearchSubTermTypeEnum, SynonymTypeEnum } from "Enums";
// styles
import styles from "./actionObject.module.scss";

type TActionObjectProps = {
    actionObject: TActionObjectDTO,
    changeActionObjectSearchPriority: (id: number, actionObjectNewSearchPriority: SearchPriorityEnum) => void,
    deleteActionObject: (id: number) => void,
    editSearchSubTerm: (id: number, newSearchSubTermValue: string, searchSubTermType: SearchSubTermTypeEnum) => void,
}

export const ActionObject: FC<TActionObjectProps> = ({ actionObject,  changeActionObjectSearchPriority,
        deleteActionObject, editSearchSubTerm }: TActionObjectProps) => {

    // State
    const [isActionSynonymsSectionShown, setIsActionSynonymsSectionShown] = useState<boolean>(false);
    const [isObjSynonymsSectionShown, setIsObjSynonymsSectionShown] = useState<boolean>(false);
    const [clickedElement, setClickedElement] = useState<{ element: EventTarget } | undefined>(undefined);

    // Logic
    const openActionSynonymsSection = (event: React.MouseEvent): void => {
        setIsActionSynonymsSectionShown(true);
        setIsObjSynonymsSectionShown(false);
        setClickedElement({ element: event.target });
    };

    const closeActionSynonymsSection = () => {
        setIsActionSynonymsSectionShown(false);
    };

    const openObjSynonymsSection = (event: React.MouseEvent): void => {
        setIsObjSynonymsSectionShown(true);
        setIsActionSynonymsSectionShown(false);
        setClickedElement({ element: event.target });
    };

    const closeObjSynonymsSection = () => {
        setIsObjSynonymsSectionShown(false);
    };

    const handleOnCheckboxChange = (checked: boolean) => {
        if (checked) {
            changeActionObjectSearchPriority(actionObject.id, SearchPriorityEnum.Should);
        } else {
            changeActionObjectSearchPriority(actionObject.id, SearchPriorityEnum.Disabled);
            setIsObjSynonymsSectionShown(false);
            setIsActionSynonymsSectionShown(false);
        }
    };

    const handleSearchTermChange = useCallback((newSearchSubTermValue: string, searchSubTermType: SearchSubTermTypeEnum) => {
        editSearchSubTerm(actionObject.id, newSearchSubTermValue, searchSubTermType);
    }, [actionObject.id, editSearchSubTerm]);

    const setClickedElementOnFocus = (event: React.FocusEvent): void => {
        setClickedElement({ element: event.target });
    };

    return (
        <div className={styles.findestActionObjectContainer}>
            <div className={actionObject.searchPriority === SearchPriorityEnum.Disabled ? [styles.findestActionObject, styles.actionObjectDisabled].join(" ") : styles.findestActionObject}>
                <div style={{display: "flex", width: "100%"}}>
                    <input title={actionObject.searchPriority !== SearchPriorityEnum.Disabled ? "Disable function" : "Enable function"} 
                        type="checkbox" checked={actionObject.searchPriority !== SearchPriorityEnum.Disabled} 
                        onChange={(event: ChangeEvent<HTMLInputElement>) => { handleOnCheckboxChange(event.target.checked); }} 
                        style={{ cursor: "pointer" }} />
                    <div className={styles.findestActionObjectInputContainer}>
                        <SearchSubTerm 
                            searchSubTermSearchPriority={actionObject.searchPriority}
                            searchSubTerm={actionObject.action} 
                            searchSubTermSynonyms={actionObject.actionSynonyms}
                            handleEditSearchSubTerm={handleSearchTermChange}
                            searchSubTermType={SearchSubTermTypeEnum.Action}
                            handleOpenSynonymsSection={openActionSynonymsSection}
                            handleCloseSynonymsSection={closeActionSynonymsSection}
                            isSynonymsSectionShown={isActionSynonymsSectionShown}
                            isDisabled={actionObject.searchPriority === SearchPriorityEnum.Disabled}
                            onTextFocus={setClickedElementOnFocus} 
                            termGroupName={actionObject.actionTermGroupName ? actionObject.actionTermGroupName : undefined} />
                        <SearchSubTerm 
                            searchSubTermSearchPriority={actionObject.searchPriority}
                            searchSubTerm={actionObject.dobject}
                            searchSubTermSynonyms={actionObject.objectSynonyms}
                            handleEditSearchSubTerm={handleSearchTermChange}
                            searchSubTermType={SearchSubTermTypeEnum.Object}
                            handleOpenSynonymsSection={openObjSynonymsSection}
                            handleCloseSynonymsSection={closeObjSynonymsSection} 
                            isSynonymsSectionShown={isObjSynonymsSectionShown}
                            isDisabled={actionObject.searchPriority === SearchPriorityEnum.Disabled}
                            onTextFocus={setClickedElementOnFocus}
                            termGroupName={actionObject.objectTermGroupName ? actionObject.objectTermGroupName : undefined} />
                    </div>
                </div>
                <div style={{display: "flex", marginLeft: "0.5rem", alignItems: "center", height: "21px"}}>
                    {actionObject.searchPriority !== SearchPriorityEnum.Disabled ? 
                            <FindestSelectSearchPriority 
                                searchPriority={actionObject.searchPriority} 
                                onChange={(newSearchPriority: SearchPriorityEnum) => changeActionObjectSearchPriority(actionObject.id, newSearchPriority)}/> 
                        : 
                            null
                    }
                    {actionObject.searchPriority !== SearchPriorityEnum.Disabled ? 
                            <div title="Delete function">   
                                <FontAwesomeIcon icon={faTrashAlt} className={styles.deleteButton} onClick={() => deleteActionObject(actionObject.id)} /> 
                            </div>
                        : 
                            null
                    }
                </div> 
            </div>
            <div className={styles.findestSynoymsContainer}>
                <div style={{display: isActionSynonymsSectionShown && !isObjSynonymsSectionShown ? "block" : "none"}}>
                    <Synonyms synonyms={actionObject.actionSynonyms} synonymType={SynonymTypeEnum.Action} 
                        parentElementId={actionObject.id} parentElementText={actionObject.action} 
                        isOpen={isActionSynonymsSectionShown} clickedParentElement={clickedElement}
                        mainTermGroupValue={actionObject.actionTermGroupName ? actionObject.action : undefined}
                        isPartOfTermGroup={!!actionObject.actionTermGroupName}  />
                </div>
                <div style={{display: isObjSynonymsSectionShown && !isActionSynonymsSectionShown? "block" : "none"}}>
                    <Synonyms synonyms={actionObject.objectSynonyms} synonymType={SynonymTypeEnum.Object} 
                        parentElementId={actionObject.id} parentElementText={actionObject.dobject} 
                        isOpen={isObjSynonymsSectionShown} clickedParentElement={clickedElement}
                        mainTermGroupValue={actionObject.objectTermGroupName ? actionObject.dobject : undefined}
                        isPartOfTermGroup={!!actionObject.objectTermGroupName}  />
                </div>
            </div>
        </div>
    );
};
